import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
    HttpStatusCodesEnum,
    OrderRouteService,
    RouteSetGetOutputInterface,
    RouteSetUpdateInputModel,
    ServerResponse,
    ServerResponseData,
    RouteSetGetOutputModel,
    RouteSearchOutputItemModel,
    RouteSearchOutputModel
} from '@spa/api';
import {
    RouteSetFormControls,
    RouteSetFormControlsInterface, RouteSetFormControlsRouteArraySelector, RouteSetFormControlsRouteIsRequired
} from 'appAssets/descriptors';
import {CheckboxControl, Helper} from 'appShared/classes';

import {map} from 'rxjs/operators';
import {OrderRoutesFormErrorStatesEnum} from '../../enums';
import {RequiredValidatorKey} from '@spa/validators';
import {Subscription} from 'rxjs';

import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums';

export class RouteSetForm {
    private orderRouteService: OrderRouteService;
    public availableRoutes: RouteSearchOutputModel;
    public formGroup: FormGroup;
    public controls: RouteSetFormControlsInterface;
    public routeSetId?: number;
    public routeSetModel: RouteSetUpdateInputModel;
    public routesErrors: string[];
    public subscriptions: Subscription[];
    public routeSetFormControlsRouteArraySelector: string;

    constructor(orderRouteService: OrderRouteService, routeSetId?: number,
                routesetModel?: RouteSetUpdateInputModel, availableRoutes?: RouteSearchOutputModel) {
        this.availableRoutes = availableRoutes;
        this.routeSetId = routeSetId;
        this.orderRouteService = orderRouteService;
        this.routeSetModel = routesetModel;
        this.subscriptions = [];
        this.routesErrors = [];
        this.routeSetFormControlsRouteArraySelector = RouteSetFormControlsRouteArraySelector;

    }

    initForm(): void {
        this.formGroup = new FormGroup({});

        this.controls = RouteSetFormControls();
        const routeIdsFormArray = new FormArray([]);
        const nonIterableControlNames = [
            RouteSetFormControlsRouteArraySelector
        ];

        if (this.availableRoutes && this.availableRoutes.total_elements > 0) {
            this.controls.routes = this.availableRoutes.content.map((r: RouteSearchOutputItemModel, index: number) => {
                const checkControl = new CheckboxControl({
                    name: index + '',
                    value: r.getId(),
                    placeholder: r.getDisplayName(),
                    nonCapitalizedPlaceholder: true,
                    validators: [],
                    componentType: ComponentTypesEnum.Checkbox,
                    checked: this.routeSetModel.getRouteIds().indexOf(r.getId()) > -1,
                    color: ColorCodesEnum.Primary,
                    expectBooleanValue: false
                });
                const checkboxFormControl = new FormControl(checkControl.value, checkControl.validators);
                routeIdsFormArray.push(checkboxFormControl);

                return checkControl;
            });
        }
        this.formGroup.addControl(RouteSetFormControlsRouteArraySelector, routeIdsFormArray);


        Object.keys(this.controls).forEach((formItemKey: string) => {
            const control = this.controls[formItemKey];
            switch (control.name) {
                case RouteSetUpdateInputModel.getExecutionBrokerIdSelector():
                    control.value = !this.routeSetModel ? null : this.routeSetModel.getExecutionBrokerId();
                    break;
                case RouteSetUpdateInputModel.getNameSelector():
                    control.value = !this.routeSetModel ? null : this.routeSetModel.getName();
                    break;
                case RouteSetFormControlsRouteArraySelector:

                    break;
            }
            if (nonIterableControlNames.indexOf(control.name) <= -1) {
                this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            }

            return control;
        });
    }

    updateRouteSet(): ServerResponse<RouteSetGetOutputModel> {
        const clonedModel = this.routeSetModel.serialize();
        const routeIds = this.formGroup.get(RouteSetFormControlsRouteArraySelector).value
            .filter((id: number) => (id !== null));
        this.routeSetModel.setName(this.formGroup.value[this.controls.name.name]);
        this.routeSetModel.setExecutionBrokerId(this.formGroup.value[this.controls.broker.name]);
        this.routeSetModel.setRouteIds(routeIds);

        return this.orderRouteService.updateRouteSet(this.routeSetId, this.routeSetModel.serialize())
            .pipe(
                map((response: ServerResponseData<null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        this.routeSetModel = new RouteSetUpdateInputModel
                        (clonedModel.execution_broker_id, clonedModel.name, clonedModel.route_ids);

                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            OrderRoutesFormErrorStatesEnum.UpdateRouteSetFailed
                        );
                    }

                    return Helper.createSuccessResponse<RouteSetGetOutputModel>(new RouteSetGetOutputModel({
                        routeIds: this.routeSetModel.getRouteIds(),
                        name: this.routeSetModel.getName(),
                        id: this.routeSetId,
                        created_at: null,
                        execution_broker_id: this.routeSetModel.getExecutionBrokerId(),
                        created_by_id: null
                    }));
                })
            );
    }

    registerRouteSet(): ServerResponse<RouteSetGetOutputModel> {
        const routeIds = this.formGroup.get(this.routeSetFormControlsRouteArraySelector).value
            .filter((id: number) => (id !== null));

        const inputModel = new RouteSetUpdateInputModel(
            this.formGroup.value[this.controls.broker.name],
            this.formGroup.value[this.controls.name.name],
            routeIds
        );

        return this.orderRouteService.createRouteSet(inputModel.serialize()).pipe(
            map((response: ServerResponseData<RouteSetGetOutputInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<RouteSetGetOutputInterface>(
                        response,
                        OrderRoutesFormErrorStatesEnum.CreateRouteSetFailed
                    );
                }
                const result = response.data as RouteSetGetOutputInterface;
                const model = new RouteSetGetOutputModel(result);
                model.setRouteIds(routeIds);

                return Helper.createSuccessResponse<RouteSetGetOutputModel>(model);
            })
        );
    }


    createRequest(): ServerResponse<RouteSetGetOutputModel> {
        if (!this.routeSetModel) {
            return this.registerRouteSet();
        }

        return this.updateRouteSet();
    }

    validateForm(messagesFunction: (controlsLabel?: string) => { [key: string]: string }): boolean {
        let isInvalidForm = false;
        if (this.formGroup.invalid) {
            Object.keys(this.controls).forEach((controlKey: string) => {
                const controlName = this.controls[controlKey].name;
                const formControl = this.formGroup.get(controlName);

                if (formControl && formControl.invalid) {
                    const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        return messagesFunction(this.controls[controlKey].placeholder)[errorKey];
                    });

                    this.controls[controlKey].setErrors(errors);
                }

            });
            isInvalidForm = true;
        }

        const routesFormControl = this.formGroup.get(RouteSetFormControlsRouteArraySelector) as FormArray;

        if (!routesFormControl) {
            isInvalidForm = true;
        } else {
            const routeIds = routesFormControl.value.filter((item: number) => item !== null);
            this.routesErrors.splice(0, this.routesErrors.length);
            if (routeIds.length === 0) {
                this.routesErrors.push(messagesFunction(RouteSetFormControlsRouteIsRequired)[RequiredValidatorKey]);

                isInvalidForm = true;
            }
        }

        return !isInvalidForm;
    }
}
